import { Device, Sound } from '@/classes';
import { useExtendFocusable } from '@/hooks/useExtendFocusable';
import { useFocusElement } from '@/hooks/useFocusElement';
import { KeyboardEventHandler, useCallback, useEffect } from 'react';

const LANGUAGE_OPTIONS = [
  { code: 'ko', name: '한국어' },
  { code: 'en', name: 'English' },
  { code: 'zh', name: '中文' },
  { code: 'pt', name: 'Português' },
  { code: 'es', name: 'Español' },
  { code: 'ja', name: '日本語' },
  { code: 'id', name: 'Bahasa Indonesia' },
];

type ButtonProps = {
  onClick: () => void;
  focusInitialize: boolean;
  message: string;
};

const Button = ({ onClick, message, focusInitialize }: ButtonProps) => {
  const { ref, focused, focusSelf } = useExtendFocusable({ isFocusBoundary: true });
  const handleClick = useCallback(() => {
    Sound.press();
    onClick();
  }, [onClick]);

  useEffect(() => {
    if (focusInitialize) {
      focusSelf();
    }
  }, [focusInitialize, focusSelf]);

  useFocusElement({ ref, focused, onClick: handleClick, keyCode: 13, propagate: false });

  return (
    <button
      ref={ref}
      className="my-2 w-[400px] h-[79px] border-[2px] border-[#CBCBCB] bg-[#E2E2E2] focus:border-primary hover:border-primary rounded-[20px] py-[4px] px-[60px] text-[30px] font-bold text-[#2B2B2B] focus:text-[#FFFFFF] hover:text-[#FFFFFF] focus:bg-primary hover:bg-primary outline-focus-none"
      onClick={handleClick}
      onContextMenu={handleClick}
    >
      {message}
    </button>
  );
};

type ChangeLanguageModalContentProps = {
  onClose: () => void;
  onChange: (language: string) => void;
};

export const ChangeLanguageModalContent = ({ onClose, onChange }: ChangeLanguageModalContentProps) => {
  const handleEvent = useCallback<KeyboardEventHandler<HTMLDivElement>>(
    (event) => {
      const isPressedCloseButton =
        event.keyCode === 10009 ||
        event.keyCode === 461 ||
        event.code === 'Backspace' ||
        event.code === 'Escape' ||
        event.key === 'Backspace' ||
        event.key === 'Escape';

      if (isPressedCloseButton) {
        Sound.press();
        event.stopPropagation();
        event.preventDefault();
        onClose();
      }
    },
    [onClose],
  );

  return (
    <div className="p-12 flex flex-col items-center" tabIndex={0} onKeyDown={handleEvent}>
      {LANGUAGE_OPTIONS.map((language) => (
        <Button
          key={language.code}
          onClick={() => onChange(language.code)}
          message={language.name}
          focusInitialize={Device.language === language.code}
        />
      ))}
    </div>
  );
};
